// assets/xero-double-hero.js
// Behavior for sections/xero-double-hero.liquid (EU port of .com's DEV-3032).
// Depends on assets/xero-utils.js (xsReducedMotion, xsWordReveal).
//
// - Word-by-word headline reveal, both halves simultaneously, on first viewport entry.
// - Ken Burns zoom on image media (CSS animation, toggled via .xs-dhero-kb).
// - Parallax: media layer scrolls slower than the page. RAF-throttled, desktop only.
// - Videos are played from JS so reduced-motion visitors keep the static poster frame.
// - Re-initializes on xs:section:init (re-broadcast of shopify:section:load).

(function() {
  'use strict';

  // The <script> tag lives in the section markup, so guard against the file
  // executing twice if the section is ever placed on a page more than once.
  if (window.__xsDoubleHeroInit) return;
  window.__xsDoubleHeroInit = true;

  var SELECTOR = '[data-section-type="xero-double-hero"]';
  // Parallax tuning: media moves at PARALLAX_SPEED x page scroll speed.
  // Travel is clamped to the CSS bleed (15% of half height) so edges never show.
  var PARALLAX_SPEED = 0.3;
  var PARALLAX_MAX_TRAVEL = 0.15;
  var DESKTOP_MQ = '(min-width: 1024px)';

  var initialized = new WeakSet();
  var parallaxSections = [];
  var parallaxScheduled = false;
  var parallaxListening = false;

  var motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

  function reducedMotion() {
    if (typeof window.xsReducedMotion === 'function') return window.xsReducedMotion();
    return motionMq.matches;
  }

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  /* ---------- Headline reveal + Ken Burns (one observer per section) ---------- */

  function observeEntry(root) {
    var headlines = root.querySelectorAll('[data-xs-word-reveal]');
    var mediaLayers = root.querySelectorAll('.xs-dhero__media');

    function reveal() {
      // Both halves animate simultaneously: identical stagger clocks started
      // in the same frame.
      if (typeof window.xsWordReveal === 'function') {
        headlines.forEach(function(el) {
          window.xsWordReveal(el, { stagger: 80, duration: 600 });
        });
      }
      if (!reducedMotion()) {
        mediaLayers.forEach(function(layer) {
          layer.classList.add('xs-dhero-kb');
        });
      }
    }

    if (!('IntersectionObserver' in window)) {
      reveal();
      return;
    }
    var io = new IntersectionObserver(function(entries, observer) {
      if (entries.some(function(entry) { return entry.isIntersecting; })) {
        reveal();
        observer.disconnect();
        root.__xsDheroEntryObserver = null;
      }
    }, { threshold: 0.2 });
    io.observe(root);
    // Stash the instance so xs:section:destroy can disconnect it if the section
    // is removed before it ever intersects (it self-disconnects otherwise).
    root.__xsDheroEntryObserver = io;
  }

  /* ---------- Video autoplay (reduced-motion users keep the poster) ---------- */

  // Visible, keyboard-accessible play/pause control per video (WCAG 2.2.2). The
  // button label/icon mirrors the real playback state via the video's own
  // play/pause events, so it stays honest no matter who started or stopped it.
  function setupVideoToggle(video) {
    var half = video.closest('.xs-dhero__half');
    var toggle = half && half.querySelector('[data-xs-dhero-toggle]');
    if (!toggle) return;

    var playLabel = toggle.getAttribute('data-xs-play-label') || 'Play video';
    var pauseLabel = toggle.getAttribute('data-xs-pause-label') || 'Pause video';

    function sync() {
      var playing = !video.paused;
      toggle.dataset.state = playing ? 'playing' : 'paused';
      toggle.setAttribute('aria-label', playing ? pauseLabel : playLabel);
    }

    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);

    toggle.addEventListener('click', function() {
      if (video.paused) {
        // Explicit opt-in plays even under reduced motion.
        video.__xsUserPaused = false;
        video.play().catch(function() {});
      } else {
        // Sticky: keep it paused even when scrolled back into view.
        video.__xsUserPaused = true;
        video.pause();
      }
    });

    sync();
  }

  function setupVideos(root) {
    var videos = root.querySelectorAll('[data-xs-dhero-video]');
    if (!videos.length) return;

    videos.forEach(function(video) {
      video.muted = true; // belt & braces: required for programmatic autoplay
      setupVideoToggle(video);

      if (!('IntersectionObserver' in window)) {
        if (!reducedMotion()) video.play().catch(function() {});
        return;
      }
      // The reduced-motion check lives at play time (not setup time) so the
      // preference is honored even when toggled after the page loaded. A manual
      // pause (__xsUserPaused) also wins over autoplay so we honor WCAG 2.2.2.
      var io = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          var v = entry.target;
          if (entry.isIntersecting && !reducedMotion() && !v.__xsUserPaused) {
            v.play().catch(function() {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.1 });
      io.observe(video);
      // Stash the instance so xs:section:destroy can disconnect it; otherwise
      // the observer outlives the section in the theme editor and leaks.
      video.__xsDheroVideoObserver = io;
    });
  }

  /* ---------- Parallax (shared RAF-throttled scroll listener) ---------- */

  function applyParallax() {
    parallaxScheduled = false;
    if (!isDesktop() || reducedMotion()) return;
    var viewportH = window.innerHeight;

    parallaxSections.forEach(function(entry) {
      if (!entry.root.isConnected) return;
      entry.layers.forEach(function(layer) {
        var half = layer.parentElement;
        var rect = half.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewportH) return;

        // Distance of the half's center from the viewport center. Counter-translating
        // by (1 - speed) makes the media scroll at `speed` x the page speed.
        var offset = rect.top + rect.height / 2 - viewportH / 2;
        var y = offset * (1 - PARALLAX_SPEED);
        var max = rect.height * PARALLAX_MAX_TRAVEL;
        y = Math.max(-max, Math.min(max, y));
        layer.style.transform = 'translate3d(0, ' + y.toFixed(1) + 'px, 0)';
      });
    });
  }

  function requestParallax() {
    if (parallaxScheduled) return;
    parallaxScheduled = true;
    requestAnimationFrame(applyParallax);
  }

  function resetParallax() {
    parallaxSections.forEach(function(entry) {
      entry.layers.forEach(function(layer) {
        layer.style.transform = '';
      });
    });
  }

  function setupParallax(root) {
    if (reducedMotion()) return;
    parallaxSections.push({
      root: root,
      layers: Array.prototype.slice.call(root.querySelectorAll('.xs-dhero__media'))
    });

    if (!parallaxListening) {
      parallaxListening = true;
      window.addEventListener('scroll', requestParallax, { passive: true });
      window.addEventListener('resize', requestParallax, { passive: true });
      window.matchMedia(DESKTOP_MQ).addEventListener('change', function(e) {
        if (e.matches) {
          requestParallax();
        } else {
          resetParallax();
        }
      });
    }
    requestParallax();
  }

  /* ---------- Init / theme editor wiring ---------- */

  function initSection(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    observeEntry(root);
    setupVideos(root);
    setupParallax(root);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: xero-utils.js re-broadcasts shopify:section:load/unload.
  document.addEventListener('xs:section:init', function(e) {
    var wrapper = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!wrapper) return;
    var root = wrapper.matches(SELECTOR) ? wrapper : wrapper.querySelector(SELECTOR);
    if (root) initSection(root);
  });

  document.addEventListener('xs:section:destroy', function(e) {
    var wrapper = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!wrapper) return;
    parallaxSections = parallaxSections.filter(function(entry) {
      return !wrapper.contains(entry.root);
    });
    var root = wrapper.matches(SELECTOR) ? wrapper : wrapper.querySelector(SELECTOR);
    if (root && root.__xsDheroEntryObserver) {
      root.__xsDheroEntryObserver.disconnect();
      root.__xsDheroEntryObserver = null;
    }
    wrapper.querySelectorAll('[data-xs-dhero-video]').forEach(function(video) {
      if (video.__xsDheroVideoObserver) {
        video.__xsDheroVideoObserver.disconnect();
        video.__xsDheroVideoObserver = null;
      }
    });
  });
})();
